import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { CustomViewCatalog, CustomViewParameterDefault, DynamicReportField, DynamicReportView } from '@/types';

/**
 * The Any Client Detail view library: every DynamicReportView row on MANDW01.ReportData, with its
 * resolved column layout one click away, and the lifecycle actions beside it — edit, clone,
 * retire/reactivate, delete. Retire and delete are deliberately two separate acts: retiring
 * removes the view from the report's dropdown (reversible from here), deleting destroys the row
 * and is only offered once it is retired.
 */
export function CustomViews() {
  const api = useApi();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['custom-view-catalog'],
    queryFn: api.customViewCatalog
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['custom-view-catalog'] });
  const onActionError = (e: unknown) =>
    setActionError(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Something went wrong.');

  const setLive = useMutation({
    mutationFn: ({ viewId, live }: { viewId: number; live: boolean }) => api.setCustomViewLive(viewId, live),
    onSuccess: () => { setActionError(null); void invalidate(); },
    onError: onActionError
  });
  const remove = useMutation({
    mutationFn: (viewId: number) => api.deleteCustomView(viewId),
    onSuccess: () => { setActionError(null); setConfirmingDelete(null); void invalidate(); },
    onError: e => { setConfirmingDelete(null); onActionError(e); }
  });

  const fieldsById = useMemo(() => {
    const map = new Map<number, DynamicReportField>();
    for (const f of data?.fields ?? []) map.set(f.fieldId, f);
    return map;
  }, [data]);

  if (isLoading) return <div className="placeholder">Loading custom views...</div>;
  if (error) return <div className="error-banner">Could not load custom views: {(error as Error).message}</div>;
  if (!data) return null;

  const saved = params.get('saved');
  const needle = filter.trim().toLowerCase();
  const views = needle
    ? data.views.filter(v =>
      v.viewDescription.toLowerCase().includes(needle)
      || (v.label ?? '').toLowerCase().includes(needle)
      || (v.notes ?? '').toLowerCase().includes(needle))
    : data.views;

  function toggle(viewId: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(viewId)) next.delete(viewId); else next.add(viewId);
      return next;
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Any Client Detail — custom views</h1>
          <div className="sub">
            Each view is one row of ReportData.dbo.DynamicReportView naming the columns it shows, in
            order. The column header IS the shared field&rsquo;s alias — renaming for one customer
            means a new field row, which is what the builder handles.
          </div>
        </div>
        <Link to="/admin/custom-views/new" className="btn-primary">New view</Link>
      </div>

      {saved && (
        <div className="notice" style={{ marginBottom: 18 }}>
          <strong>&ldquo;{saved}&rdquo; saved.</strong>
          The view is live and available in the report&rsquo;s View dropdown.
          <button
            className="btn-secondary cv-btn"
            style={{ marginLeft: 10 }}
            onClick={() => { const next = new URLSearchParams(params); next.delete('saved'); setParams(next, { replace: true }); }}>
            Dismiss
          </button>
        </div>
      )}

      {actionError && (
        <div className="error-banner" style={{ marginBottom: 18 }}>{actionError}</div>
      )}

      <SourceBanner catalog={data} />

      <div className="list-filters">
        <input
          className="input"
          style={{ maxWidth: 360 }}
          type="search"
          placeholder="Search views by name, label or notes..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>

      <div className="card">
        <table className="data-table cv-library">
          <thead>
            {/* table-layout is fixed, so these widths ARE the columns — View takes the rest. */}
            <tr>
              <th style={{ width: 56 }}>ViewId</th>
              <th>View</th>
              <th style={{ width: 72 }}>Columns</th>
              <th style={{ width: 112 }}>Audience</th>
              <th style={{ width: 88 }}>Status</th>
              <th style={{ width: 216 }} />
            </tr>
          </thead>
          <tbody>
            {views.length === 0 && (
              <tr>
                <td colSpan={6} className="placeholder">
                  {data.views.length === 0
                    ? 'No views to show — the catalogue is empty.'
                    : `No view matches "${filter}".`}
                </td>
              </tr>
            )}
            {views.map(v => (
              <ViewRow
                key={v.viewId}
                view={v}
                open={expanded.has(v.viewId)}
                onToggle={() => toggle(v.viewId)}
                fieldsById={fieldsById}
                parameterDefaults={data.parameterDefaults}
                busy={setLive.isPending || remove.isPending}
                confirmingDelete={confirmingDelete === v.viewId}
                onSetLive={live => setLive.mutate({ viewId: v.viewId, live })}
                onDelete={() => (confirmingDelete === v.viewId
                  ? remove.mutate(v.viewId)
                  : setConfirmingDelete(v.viewId))}
                onCancelDelete={() => setConfirmingDelete(null)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SourceBanner({ catalog }: { catalog: CustomViewCatalog }) {
  if (catalog.source === 'unconfigured') {
    return (
      <div className="notice notice-warn" style={{ marginBottom: 18 }}>
        <strong>The ReportData catalogue could not be read.</strong>
        The portal&rsquo;s Database Access caller has no grant on <code>reportdata</code> (or no
        subscription key is configured), so there is nothing to show and nothing can be changed.
      </div>
    );
  }
  if (catalog.source === 'sample') {
    return (
      <div className="notice notice-warn" style={{ marginBottom: 18 }}>
        <strong>Dev sandbox — MANDW01 not reachable.</strong>
        A representative in-memory catalogue is shown and every change applies to it, so the whole
        flow is exercisable off-VPN. Nothing here touches the live configuration.
      </div>
    );
  }
  return (
    <div className="sub" style={{ marginBottom: 18 }}>
      Live from MANDW01.ReportData &middot; {catalog.views.length} views &middot;{' '}
      {catalog.fields.length} selectable fields
    </div>
  );
}

function ViewRow({ view, open, onToggle, fieldsById, parameterDefaults, busy, confirmingDelete, onSetLive, onDelete, onCancelDelete }: {
  view: DynamicReportView;
  open: boolean;
  onToggle: () => void;
  fieldsById: Map<number, DynamicReportField>;
  parameterDefaults?: CustomViewParameterDefault[] | null;
  busy: boolean;
  confirmingDelete: boolean;
  onSetLive: (live: boolean) => void;
  onDelete: () => void;
  onCancelDelete: () => void;
}) {
  const columnCount = fieldIds(view.fieldIdList).length;
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td data-label="ViewId">{view.viewId}</td>
        <td data-label="View">
          <div className="report-name" title={view.viewDescription}>{view.viewDescription}</div>
          <SecondaryLine view={view} />
          {(view.hasFilterSql || view.hasTransformSql) && (
            <div className="chip-row">
              {view.hasFilterSql && (
                <span className="chip chip-folder" style={{ color: 'var(--kt-orange)' }}
                  title="This view carries FilterSQL — it returns a subset of rows. Clones inherit it; the portal never edits it.">
                  Filter{view.filterDescription ? `: ${view.filterDescription}` : ''}
                </span>
              )}
              {view.hasTransformSql && (
                <span className="chip chip-folder" style={{ color: 'var(--kt-orange)' }}
                  title="This view carries TransformSQL — its output is reshaped after selection. Clones inherit it; the portal never edits it.">
                  Transform{view.transformDescription ? `: ${view.transformDescription}` : ''}
                </span>
              )}
            </div>
          )}
        </td>
        <td data-label="Columns">
          {columnCount}
          {view.distinct && (
            <span className="chip chip-folder" style={{ marginLeft: 6 }}
              title="Distinct: rows are grouped; the Σ columns in the expanded layout are aggregated">
              Σ
            </span>
          )}
        </td>
        <td data-label="Audience"><Audience view={view} /></td>
        <td data-label="Status">
          {view.live
            ? <span className="pill pill-success">Live</span>
            : <span className="pill pill-disabled">Retired</span>}
        </td>
        <td onClick={e => e.stopPropagation()}>
          {confirmingDelete ? (
            // Confirm mode REPLACES the row's actions: two buttons always fit on one line, and
            // there is nothing else to mis-click while a delete is armed.
            <div className="cv-actions">
              <button className="btn-danger cv-btn" disabled={busy} onClick={onDelete}>Confirm delete</button>
              <button className="btn-secondary cv-btn" onClick={onCancelDelete}>Cancel</button>
            </div>
          ) : (
            <div className="cv-actions">
              <Link className="btn-secondary cv-btn" to={`/admin/custom-views/${view.viewId}/edit`}>Edit</Link>
              <Link
                className="btn-secondary cv-btn"
                to={`/admin/custom-views/new?base=${view.viewId}`}
                title="Start a new view from this one's columns">
                Clone
              </Link>
              {view.live ? (
                <button className="btn-secondary cv-btn" disabled={busy}
                  title="Remove from the report's View dropdown; keeps the view and its columns"
                  onClick={() => onSetLive(false)}>
                  Retire
                </button>
              ) : (
                <button className="btn-secondary cv-btn" disabled={busy} onClick={() => onSetLive(true)}>
                  Reactivate
                </button>
              )}
              <button
                className="btn-secondary cv-btn"
                disabled={busy || view.live}
                title={view.live
                  ? 'A live view cannot be deleted — retire it first'
                  : 'Delete the view row permanently (its shared field rows are kept)'}
                onClick={onDelete}>
                Delete
              </button>
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--kt-grey-50)' }}>
            <ResolvedColumns view={view} fieldsById={fieldsById} />
            <ParameterDefaults view={view} defaults={parameterDefaults} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The View cell's one-line context: the report header when it differs from the view's name
 * (a separate column wasted width repeating "Client Detail" on ~every row and wrapped badly
 * on the few long ones), then the notes. Ellipsised by .cv-note; the title carries it all.
 */
function SecondaryLine({ view }: { view: DynamicReportView }) {
  const headerDiffers = !!view.label && view.label.trim() !== view.viewDescription.trim();
  if (!headerDiffers && !view.notes) return null;
  const full = `${headerDiffers ? `Report header: ${view.label}` : ''}${headerDiffers && view.notes ? ' — ' : ''}${view.notes ?? ''}`;
  return (
    <div className="cv-note" title={full}>
      {headerDiffers && <>Header: {view.label}{view.notes ? ' — ' : ''}</>}
      {view.notes}
    </div>
  );
}

/**
 * Who can pick the view in the report's dropdown, in two words — the detail lives in the
 * tooltip. UserList, when present, overrides the groups entirely: the one that surprises people.
 */
function Audience({ view }: { view: DynamicReportView }) {
  if (view.userList?.trim()) {
    return (
      <span className="cv-audience" title={`UserList: ${view.userList} (overrides security groups entirely)`}>
        Named users
      </span>
    );
  }
  if (view.securityGroups?.trim()) {
    const count = view.securityGroups.split(',').length;
    return <span className="cv-audience" title={view.securityGroups}>{count} group{count === 1 ? '' : 's'}</span>;
  }
  return (
    <span className="cv-audience" style={{ color: 'var(--kt-orange)' }}
      title="No SecurityGroups and no UserList — only SG DW Admin members can see it">
      Admins only
    </span>
  );
}

/** The view's layout, resolved client-side by joining FieldIdList against the shared field list. */
function ResolvedColumns({ view, fieldsById }: {
  view: DynamicReportView;
  fieldsById: Map<number, DynamicReportField>;
}) {
  const ids = fieldIds(view.fieldIdList);
  const aggregated = new Set(fieldIds(view.aggregateFieldIdList ?? ''));
  return (
    <div style={{ padding: '10px 4px' }}>
      <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginBottom: 6 }}>
        Columns, in display order (FieldId in brackets; hover for the source SQL
        {aggregated.size > 0 && <> &middot; Σ = aggregated with the field&rsquo;s function, default MAX</>}):
      </div>
      <div className="chip-row">
        {ids.map((id, i) => {
          const field = fieldsById.get(id);
          const fn = field?.aggregateFunction ?? 'MAX';
          return (
            <span
              key={`${id}-${i}`}
              className="chip"
              title={field
                ? `${field.tableAlias ?? ''} ${field.fieldSql}`.trim() + (aggregated.has(id) ? ` — aggregated: ${fn}` : '')
                : 'Unknown field'}>
              {i + 1}. {field ? field.fieldAlias : `FieldId ${id}?`}
              {aggregated.has(id) && <span style={{ color: 'var(--kt-green)' }}> Σ</span>}
              {' '}<span style={{ color: 'var(--kt-grey-400)' }}>({id})</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The view's per-view parameter defaults. Three honest states: rows, none, and — when the whole
 * list is null — "could not be read", which must never be presented as "none".
 */
function ParameterDefaults({ view, defaults }: {
  view: DynamicReportView;
  defaults?: CustomViewParameterDefault[] | null;
}) {
  if (defaults === null || defaults === undefined) {
    return (
      <div style={{ padding: '0 4px 10px', fontSize: '.8rem', color: 'var(--kt-orange)' }}>
        Parameter defaults could not be read from dbo.DynamicReportParameterDefault. They may still
        exist — check the table directly.
      </div>
    );
  }
  const mine = defaults.filter(d => d.viewId === view.viewId);
  return (
    <div style={{ padding: '0 4px 10px', fontSize: '.8rem', color: 'var(--kt-grey-500)' }}>
      Parameter defaults:{' '}
      {mine.length === 0
        ? 'none (the report asks for everything as usual)'
        : mine.map((d, i) => (
          <span key={`${d.parameter}-${i}`} title={d.notes ?? undefined} style={{ marginRight: 10 }}>
            <strong>{d.parameter}</strong> ={' '}
            {d.defaultValueSql
              // An expression, not a value — the note ("Start of next week (Mon)") says it better
              // than the SQL does, so lead with it and keep the SQL on hover.
              ? <em>{d.notes || 'computed'}</em>
              : truncate(d.defaultValue ?? 'NULL', 40)}
            {d.isMultiSelect && ' (multi)'}
          </span>
        ))}
      {mine.length > 0 && <> — copied to clones made from this view</>}
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function fieldIds(fieldIdList: string): number[] {
  return fieldIdList
    .split(',')
    .map(t => Number.parseInt(t.trim(), 10))
    .filter(n => Number.isFinite(n));
}
